import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FileXls, Gear } from "@phosphor-icons/react";
import ExcelUploadZone from "./sheetsSync/ExcelUploadZone";
import UploadPreviewTable from "./sheetsSync/UploadPreviewTable";
import UploadHistoryTable from "./sheetsSync/UploadHistoryTable";
import LastUploadSummary from "./sheetsSync/LastUploadSummary";
import MappingDialog from "./sheetsSync/MappingDialog";
import { UNMAPPED } from "./sheetsSync/constants";

export default function UploadRealisasi() {
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [applyingUpload, setApplyingUpload] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [logs, setLogs] = useState([]);

  // Mapping state — loaded once + editable via dialog
  const [mapping, setMapping] = useState({});
  const [mappingKeys, setMappingKeys] = useState({ keys_required: [], keys_optional: [] });
  const [mappingDialog, setMappingDialog] = useState(false);
  const [mappingDraft, setMappingDraft] = useState({});
  const [savingMapping, setSavingMapping] = useState(false);

  const loadLogs = async () => {
    try {
      const { data } = await api.get("/sheets/logs", { params: { limit: 20 } });
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      setLogs([]);
    }
  };
  useEffect(() => { loadLogs(); /* one-shot on mount */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load persisted mapping on mount
  useEffect(() => {
    api.get("/sheets/upload-mapping")
      .then(({ data }) => {
        setMapping(data.mapping || {});
        setMappingKeys({
          keys_required: data.keys_required || [],
          keys_optional: data.keys_optional || [],
        });
      })
      .catch(() => {
        setMappingKeys({
          keys_required: ["no_polisi", "nama_barang", "jumlah", "distrik"],
          keys_optional: ["no_spb", "no_so"],
        });
      });
  }, []);

  const openMappingDialog = () => {
    // Seed the draft from persisted mapping; fill any REQUIRED gaps from the
    // preview's auto-suggestion so the user can just confirm.
    const suggested = uploadPreview?.suggested_mapping || {};
    const seeded = { ...mapping };
    for (const [k, v] of Object.entries(suggested)) {
      if (!seeded[k] && v) seeded[k] = v;
    }
    setMappingDraft(seeded);
    setMappingDialog(true);
  };

  const applyAutoDetect = () => {
    const suggested = uploadPreview?.suggested_mapping || {};
    if (!Object.keys(suggested).length) {
      toast.info("Upload file dulu supaya auto-detect punya nama kolom");
      return;
    }
    setMappingDraft((d) => {
      const next = { ...d };
      for (const [k, v] of Object.entries(suggested)) {
        if (v && (!next[k] || next[k] === UNMAPPED)) next[k] = v;
      }
      return next;
    });
    toast.success("Mapping otomatis diisi dari fuzzy match kolom");
  };

  const saveMapping = async () => {
    setSavingMapping(true);
    try {
      const cleaned = {};
      for (const [k, v] of Object.entries(mappingDraft)) {
        if (v && v !== UNMAPPED) cleaned[k] = v;
      }
      const { data } = await api.post("/sheets/upload-mapping", { mapping: cleaned });
      setMapping(data.mapping || cleaned);
      setMappingDialog(false);
      toast.success("Mapping disimpan. Upload ulang untuk pakai mapping baru.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingMapping(false);
    }
  };

  const runPreview = async (file) => {
    const name = (file?.name || "").toLowerCase();
    if (!(name.endsWith(".xlsx") || name.endsWith(".xlsm"))) {
      toast.error("File harus .xlsx atau .xlsm");
      return;
    }
    setUploading(true);
    setUploadPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("limit", "500");
      const { data } = await api.post("/sheets/upload-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadPreview(data);
      toast.success(`Preview siap · ${data.total_rows_in_file} baris dari "${data.filename}"`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const onUploadApply = async () => {
    if (!uploadPreview?.upload_id) return;
    const c = uploadPreview.counters || {};
    const proceed = window.confirm(
      `Terapkan ${c.would_update || 0} update dari "${uploadPreview.filename}"?\n\n` +
      `Akan skip: ${c.already_verified || 0} sudah verified, ${c.skipped || 0} tidak match, ${c.not_found || 0} tidak ditemukan.`
    );
    if (!proceed) return;
    setApplyingUpload(true);
    try {
      const { data } = await api.post("/sheets/upload-apply", { upload_id: uploadPreview.upload_id });
      const rc = data.counters || {};
      toast.success(
        `Selesai · ${rc.updated || 0} update · ${rc.mismatch || 0} mismatch · ${rc.already_verified || 0} sudah verified`,
      );
      setUploadPreview(null);
      loadLogs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApplyingUpload(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl">
      <div className="border-b border-border pb-6 mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="overline">Integrasi</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <FileXls size={30} weight="bold" /> Upload Realisasi Excel
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Upload file <span className="font-mono font-bold">.xlsx</span> hasil export ERP. Sistem cocokkan otomatis dengan SPB pakai matcher yang sama (plat + jumlah + barang + alias produk).
          </p>
        </div>
        <Button variant="outline" onClick={openMappingDialog} className="rounded-none" data-testid="open-mapping-dialog">
          <Gear size={16} className="mr-2" /> Mapping Kolom
        </Button>
      </div>

      {logs.length > 0 && !uploadPreview && <LastUploadSummary log={logs[0]} />}

      <ExcelUploadZone
        uploading={uploading}
        uploadPreview={uploadPreview}
        dragOver={dragOver}
        setDragOver={setDragOver}
        onFileSelected={runPreview}
      />

      <UploadPreviewTable
        uploadPreview={uploadPreview}
        applyingUpload={applyingUpload}
        onCancel={() => setUploadPreview(null)}
        onApply={onUploadApply}
      />

      <UploadHistoryTable logs={logs} />

      <MappingDialog
        open={mappingDialog}
        onOpenChange={setMappingDialog}
        uploadPreview={uploadPreview}
        mappingKeys={mappingKeys}
        mappingDraft={mappingDraft}
        setMappingDraft={setMappingDraft}
        savingMapping={savingMapping}
        onSave={saveMapping}
        onAutoDetect={applyAutoDetect}
      />
    </div>
  );
}
